import { useState, useEffect, useRef, ReactNode, FormEvent, Suspense, RefObject } from 'react';
import type { ComponentProps, FocusEvent, KeyboardEvent } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { LocaleCode } from '@ifixit/i18n/types';
// Import from our reorganized search directory
import { SearchInput } from '../search/components/search-input';
// Types for search functionality
import type { SearchClientType } from '../search/types';
// Import Radix UI Popover types for proper typing
import type * as PopoverPrimitive from '@radix-ui/react-popover';
// Dynamic imports - centralized in a single file
import {
   DynamicSearchProvider,
   DynamicSearchContent,
   DynamicPopover,
   DynamicPopoverTrigger,
   DynamicPopoverContent,
   preloadAllSearchComponents,
} from '../search/utils/dynamic-imports';
import { getSearchUrl } from '../search/utils';
import { useSearchContext } from '../search/components/search-provider';
import { type Doctype } from '@ifixit/helpers';
import { SeeAllResultsFooter } from '../search/components/search-content';
import { useShortcut } from '@ifixit/utils-react/hooks';

// Types
// ---------------------------------------------------------------------------

type PointerDownOutsideEvent = CustomEvent<{ originalEvent: PointerEvent }>;
type FocusOutsideEvent = CustomEvent<{ originalEvent: globalThis.FocusEvent }>;

/**
 * Props for the InstantSearch component
 */
export interface InstantSearchProps {
   canPopoverOpen?: boolean;
   /** Placeholder text for the search input */
   placeholder?: string;
   /** Initialized Algolia search client */
   searchClient: SearchClientType;
   /** Record mapping document types to arrays of index names */
   indices: Partial<Record<Doctype, string[]>>;
   /** Algolia public search API key */
   apiKey: string;
   isProUser: boolean;
   /** Ordered list of document types to control display order */
   inputProps?: ComponentProps<typeof SearchInput>['inputProps'];
   /** Additional props to pass to the Popover component */
   popoverProps?: PopoverPrimitive.PopoverContentProps;
   /** Optional content to render after the input (like keyboard shortcut indicator) */
   inputAfter?: ReactNode;
   /** Additional class names to apply to the root container */
   className?: string;
   /** Currency code for product prices */
   currency: string;
   /** Locale code for internationalization */
   localeCode: LocaleCode;
   /** Store code for shop-specific content */
   storeCode: string;
   /** Language identifier, defaults to 'en' */
   langid?: string;
   shortcutKey?: string;
   /** Autofocus the search input on mount */
   autoFocus?: boolean;
}

// Lazy-loaded components
// ---------------------------------------------------------------------------

/**
 * Custom hook to synchronize search state between provider and local component
 */
function useSyncSearchState(localQuery: string, setIsSearching: (value: boolean) => void) {
   const { query, setQuery, isSearching, hasResults } = useSearchContext();
   // Sync the provider's query with our local state
   useEffect(() => {
      if (query !== localQuery) {
         setQuery(localQuery);
      }
   }, [localQuery, setQuery, query]);

   // Update our local state from provider
   useEffect(() => {
      setIsSearching(isSearching);
   }, [isSearching, hasResults, localQuery, setIsSearching]);
}

/**
 * Consistent popover styling classes
 */
const POPOVER_STYLES = cn(
   'p-0 bg-white',
   'sm:min-w-[474px] z-100 sm:overflow-initial overflow-y-auto',
   'rounded-none sm:rounded-2xl w-screen sm:w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-content-available-width)] mx-auto shadow-2xl'
);

/**
 * Loading overlay component that covers the entire search container
 */
function LoadingOverlay({ children, isLoading }: { children: ReactNode; isLoading: boolean }) {
   return (
      <>
         {children}
         {isLoading && (
            <div
               className="absolute inset-0 bg-gray-50/50 flex items-center justify-center transition-all duration-200 ease-in-out z-10 pointer-events-none"
               data-testid="loading-overlay"
               role="status"
               aria-live="polite"
            />
         )}
      </>
   );
}

/**
 * Container for search results content
 */
function SearchResultsContainer({
   currency,
   localeCode,
   storeCode,
}: {
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
}) {
   const { isSearching } = useSearchContext();

   return (
      <LoadingOverlay isLoading={isSearching}>
         <div className="sm:max-h-[min(calc(var(--radix-popover-content-available-height)-42px),648px)] h-inherit overflow-x-hidden">
            <div
               className={cn(
                  'content flex flex-col items-start gap-5 self-stretch overflow-y-auto',
                  'max-w-[100vw] h-inherit sm:w-auto',
                  '[scrollbar-gutter:stable_both-edges] sm:[scrollbar-gutter:auto]'
               )}
            >
               <DynamicSearchContent
                  currency={currency}
                  localeCode={localeCode}
                  storeCode={storeCode}
               />
            </div>
            <SeeAllResultsFooter />
         </div>
      </LoadingOverlay>
   );
}

/**
 * Custom hook to preload components and manage loading state
 */
function useComponentPreloading() {
   const [componentsLoaded, setComponentsLoaded] = useState(false);

   useEffect(() => {
      void preloadAllSearchComponents().then(() => {
         setComponentsLoaded(true);
      });
   }, []);

   return componentsLoaded;
}

/**
 * Creates form submission handler
 */
function createSubmitHandler(localQuery: string, hasSearchText: boolean) {
   return (e: FormEvent) => {
      e.preventDefault();
      if (hasSearchText) {
         window.location.href = getSearchUrl({ searchStr: localQuery });
      }
   };
}

/**
 * Creates keyboard event handlers for the search input
 */
function createKeyboardHandlers(
   inputProps: ComponentProps<typeof SearchInput>['inputProps'],
   popoverRef: RefObject<HTMLDivElement | null>,
   inputRef: RefObject<HTMLInputElement | null>,
   clearSearch: () => void
) {
   return {
      ...inputProps,
      onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
         const passedInKeyDown = inputProps?.onKeyDown || null;
         if (passedInKeyDown) {
            passedInKeyDown(e);
         }
         if (e.key === 'Tab') {
            e.preventDefault();
            popoverRef.current?.focus();
         } else if (e.key === 'Escape') {
            inputRef.current?.blur();
            clearSearch();
         }
      },
   };
}

/**
 * Manages search result popover display and state synchronization
 */
function SearchResultsPopover({
   localQuery,
   setIsSearching,
   popoverRef,
   popoverProps,
   currency,
   localeCode,
   storeCode,
   isPopoverOpen,
   setIsPopoverOpen,
}: {
   localQuery: string;
   setIsSearching: (value: boolean) => void;
   popoverRef: RefObject<HTMLDivElement | null>;
   popoverProps?: PopoverPrimitive.PopoverContentProps;
   currency: string;
   localeCode: LocaleCode;
   storeCode: string;
   isPopoverOpen: boolean;
   setIsPopoverOpen: (open: boolean) => void;
}) {
   const { hasSearched } = useSearchContext();

   // Keep search provider and local state in sync
   useSyncSearchState(localQuery, setIsSearching);

   return (
      <DynamicPopover open={isPopoverOpen && hasSearched} onOpenChange={setIsPopoverOpen}>
         <DynamicPopoverTrigger asChild>
            <div className="absolute w-full" />
         </DynamicPopoverTrigger>
         <DynamicPopoverContent
            className={POPOVER_STYLES}
            align="center"
            sideOffset={4}
            onOpenAutoFocus={e => e.preventDefault()}
            data-testid="popover-content"
            ref={popoverRef}
            {...popoverProps}
         >
            <SearchResultsContainer
               currency={currency}
               localeCode={localeCode}
               storeCode={storeCode}
            />
         </DynamicPopoverContent>
      </DynamicPopover>
   );
}

/**
 * Main InstantSearch component
 *
 * This component provides a search input with a dropdown of search results.
 * It uses dynamic imports for the search functionality and UI components to
 * improve initial page load performance.
 *
 * The component renders the search input immediately and loads the search
 * results dropdown dynamically when the input is focused.
 */
export function InstantSearch({
   placeholder,
   searchClient,
   apiKey,
   indices,
   inputProps,
   popoverProps,
   inputAfter,
   className,
   currency,
   localeCode,
   storeCode,
   langid = 'en',
   canPopoverOpen = true,
   shortcutKey,
   isProUser,
   autoFocus = false,
}: InstantSearchProps) {
   // Component state
   const inputRef = useRef<HTMLInputElement>(null);
   const popoverRef = useRef<HTMLDivElement>(null);
   const [localQuery, setLocalQuery] = useState('');
   const [isSearching, setIsSearching] = useState(false);
   const [isPopoverOpen, setIsPopoverOpen] = useState(false);

   // Autofocus effect
   useEffect(() => {
      if (autoFocus && inputRef.current) {
         inputRef.current.focus();
      }
   }, [autoFocus]);

   // Component loading management
   const componentsLoaded = useComponentPreloading();

   // Derived state
   const hasSearchText = localQuery.trim() !== '';
   const showLoadingSpinner = isSearching || (hasSearchText && !componentsLoaded);

   // Event handlers
   const handleSubmit = createSubmitHandler(localQuery, hasSearchText);
   const clearSearch = () => setLocalQuery('');

   const handleInputFocus = (e: FocusEvent<HTMLInputElement>) => {
      setIsPopoverOpen(true);
      if (inputProps && inputProps.onFocus) {
         inputProps.onFocus(e);
      }
   };
   const keyboardProps = {
      // eslint-disable-next-line react-hooks/refs
      ...createKeyboardHandlers(inputProps, popoverRef, inputRef, clearSearch),
      onFocus: handleInputFocus,
      autoFocus,
   };

   // Shortcut functionality
   const ShortcutIndicator = useShortcutIndicator(shortcutKey, inputRef);

   const mergedPopoverProps = {
      ...popoverProps,
      onPointerDownOutside: (e: PointerDownOutsideEvent) => {
         e.preventDefault();
         if (isPointerEventWithinInput(e, inputRef.current)) {
            setIsPopoverOpen(true);
            return;
         }
         setIsPopoverOpen(false);
         if (popoverProps && popoverProps.onPointerDownOutside) {
            popoverProps.onPointerDownOutside(e);
         }
      },
      onFocusOutside: (e: FocusOutsideEvent) => {
         // Prevent popover from closing when focus moves outside the document
         // (e.g., switching tabs or windows)
         e.preventDefault();
      },
   };

   // Open popover if there are results and popover is open
   const shouldShowPopover = componentsLoaded && canPopoverOpen && isPopoverOpen;

   return (
      <div className={cn('relative flex-grow', className)} onClick={e => e.stopPropagation()}>
         <div className="w-full relative">
            <SearchInput
               query={localQuery}
               setQuery={setLocalQuery}
               clearSearch={clearSearch}
               placeholder={placeholder || ''}
               inputRef={inputRef}
               popoverLoading={showLoadingSpinner}
               inputProps={keyboardProps}
               inputAfter={inputAfter || ShortcutIndicator}
               onSubmit={handleSubmit}
            />

            {shouldShowPopover && (
               <Suspense fallback={null}>
                  <DynamicSearchProvider
                     searchOptions={{
                        searchClient,
                        apiKey,
                        indices,
                        langid,
                        isProUser,
                     }}
                  >
                     <SearchResultsPopover
                        localQuery={localQuery}
                        setIsSearching={setIsSearching}
                        popoverRef={popoverRef}
                        popoverProps={mergedPopoverProps}
                        currency={currency}
                        localeCode={localeCode}
                        storeCode={storeCode}
                        isPopoverOpen={isPopoverOpen}
                        setIsPopoverOpen={setIsPopoverOpen}
                     />
                  </DynamicSearchProvider>
               </Suspense>
            )}
         </div>
      </div>
   );
}

/**
 * Visual indicator showing the keyboard shortcut for focusing the search input
 */
function SearchInputShortcut({ shortcutKey = '/' }: { shortcutKey?: string }) {
   return (
      <div
         className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
         data-testid="shortcut-hint"
      >
         <div className="bg-gray-300 rounded-full px-2.5 py-1 text-xs text-gray-800">
            {shortcutKey}
         </div>
      </div>
   );
}

/**
 * Hook that provides keyboard shortcut functionality and returns the shortcut indicator
 */
function useShortcutIndicator(
   shortcutKey: string | undefined,
   inputRef: RefObject<HTMLInputElement | null>
) {
   const callback = (input: HTMLInputElement, event: globalThis.KeyboardEvent) => {
      input.focus();
      event.stopPropagation();
      event.preventDefault();
   };
   useShortcut({ key: shortcutKey }, inputRef, callback);
   return shortcutKey ? <SearchInputShortcut shortcutKey={shortcutKey} /> : null;
}

/**
 * Returns true if the pointer event target is within the input element.
 */
function isPointerEventWithinInput(
   e: PointerDownOutsideEvent,
   inputEl: HTMLInputElement | null
): boolean {
   if (!inputEl) return false;
   const eventTarget = e?.detail?.originalEvent?.target as Node | null;
   return !!(eventTarget && inputEl.contains(eventTarget));
}
